import type { JSX, ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { addToast, Button, ButtonGroup, Spinner } from '@heroui/react';
import { AlertCircle, Check, Copy, Eye, FileCode2, List, PanelRightClose } from 'lucide-react';
import remarkGfm from 'remark-gfm';

import { useDocumentContent } from '~/hooks/useDocumentContent';
import { titleToId } from '~/utils/markdown';

import { CodeBlock } from '~/pages/Documentation/components/ui/CodeBlock';

import type { DocumentType } from '~/types';

import { MermaidDiagram } from './MermaidDiagram';
import { Modal } from './Modal';

type ViewMode = 'raw' | 'rendered';

interface DocumentViewerDialogProps {
  /** Document type to fetch. Required when not providing directContent. */
  documentType?: DocumentType;
  isOpen: boolean;
  onClose: () => void;
  /** Project ID for fetching document. Required when not providing directContent. */
  projectId?: string;
  title: string;
  /** When provided, displays this content directly instead of fetching from API. */
  directContent?: string | null;
  /** Loading state for directContent mode. */
  isContentLoading?: boolean;
  /** Error for directContent mode. */
  contentError?: unknown;
}

interface TocEntry {
  id: string;
  level: number;
  text: string;
}

interface TocSidebarProps {
  entries: TocEntry[];
  isOpen: boolean;
  onSelect: (id: string) => void;
  onToggle: () => void;
}

/** Extract text content from react-markdown children nodes */
function extractTextFromChildren(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return extractTextFromChildren(
      (children as { props: { children?: ReactNode } }).props.children,
    );
  }
  return '';
}

function TocSidebar({ entries, isOpen, onSelect, onToggle }: TocSidebarProps): JSX.Element {
  const indentClass: Record<number, string> = {
    1: 'pl-0',
    2: 'pl-3',
    3: 'pl-5',
  };

  return (
    <div
      className={`flex-shrink-0 border-l border-default transition-all duration-200 ${isOpen ? 'w-52' : 'w-8'}`}
    >
      <div className="sticky top-0 flex items-center gap-2 py-1 px-2">
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-default-100 text-muted hover:text-foreground transition-colors"
          aria-label={isOpen ? 'Collapse table of contents' : 'Expand table of contents'}
        >
          {isOpen ? <PanelRightClose className="w-4 h-4" /> : <List className="w-4 h-4" />}
        </button>
        {isOpen && (
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
            Document Outline
          </span>
        )}
      </div>
      {isOpen && (
        <nav className="overflow-y-auto px-2 pb-4" aria-label="Table of contents">
          <ul className="space-y-0.5">
            {entries.map(entry => (
              <li key={entry.id}>
                <button
                  onClick={() => onSelect(entry.id)}
                  className={`w-full text-left text-xs py-1 px-1.5 rounded truncate text-muted hover:text-foreground hover:bg-default-100 transition-colors ${indentClass[entry.level] ?? 'pl-0'}`}
                  title={entry.text}
                >
                  {entry.text}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

export function DocumentViewerDialog({
  contentError,
  directContent,
  documentType,
  isContentLoading,
  isOpen,
  onClose,
  projectId,
  title,
}: DocumentViewerDialogProps): JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>('rendered');
  const [isCopied, setIsCopied] = useState(false);
  const [isTocOpen, setIsTocOpen] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const useDirectContent = directContent !== undefined;

  const {
    content: fetchedContent,
    error: fetchError,
    isLoading: fetchLoading,
    refetch,
  } = useDocumentContent(
    projectId ?? null,
    documentType ?? 'technical-spec',
    isOpen && !useDirectContent,
  );

  const content = useDirectContent
    ? directContent
      ? { content: directContent }
      : null
    : fetchedContent;
  const error = useDirectContent ? contentError : fetchError;
  const isLoading = useDirectContent ? (isContentLoading ?? false) : fetchLoading;

  const tocEntries = useMemo<TocEntry[]>(() => {
    if (!content?.content) return [];
    const entries: TocEntry[] = [];
    const lines = content.content.split('\n');
    for (const line of lines) {
      const match = /^(#{1,3})\s+(.+)$/.exec(line.trim());
      if (match) {
        entries.push({
          level: match[1].length,
          text: match[2],
          id: titleToId(match[2]),
        });
      }
    }
    return entries;
  }, [content?.content]);

  const handleCopy = useCallback(async () => {
    if (!content?.content) return;

    try {
      await navigator.clipboard.writeText(content.content);
      setIsCopied(true);
      addToast({ color: 'success', description: 'Copied to clipboard' });
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      addToast({ color: 'danger', description: 'Failed to copy to clipboard' });
    }
  }, [content?.content]);

  const scrollToHeading = useCallback((id: string) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const el = container.querySelector(`#${CSS.escape(id)}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const showToc = viewMode === 'rendered' && tocEntries.length > 0 && !isLoading && !error;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="5xl" header={title}>
      {/* Toolbar: view toggle + copy button */}
      <div className="flex items-center justify-between mb-4">
        <ButtonGroup size="sm" variant="flat">
          <Button
            color={viewMode === 'raw' ? 'primary' : 'default'}
            variant={viewMode === 'raw' ? 'solid' : 'flat'}
            onPress={() => setViewMode('raw')}
            aria-pressed={viewMode === 'raw'}
            startContent={<FileCode2 className="w-4 h-4" />}
          >
            Raw
          </Button>
          <Button
            color={viewMode === 'rendered' ? 'primary' : 'default'}
            variant={viewMode === 'rendered' ? 'solid' : 'flat'}
            onPress={() => setViewMode('rendered')}
            aria-pressed={viewMode === 'rendered'}
            startContent={<Eye className="w-4 h-4" />}
          >
            Rendered
          </Button>
        </ButtonGroup>

        <Button
          size="sm"
          variant="flat"
          isDisabled={!content?.content}
          onPress={() => void handleCopy()}
          aria-label="Copy markdown to clipboard"
          startContent={
            isCopied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />
          }
        >
          {isCopied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {/* Content area with optional TOC sidebar */}
      <div className="flex gap-0 overflow-hidden" style={{ maxHeight: '70vh' }}>
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-w-0 pr-2">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" label="Loading document..." />
            </div>
          )}

          {!!error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <AlertCircle className="w-10 h-10 text-danger" />
              <p className="text-foreground">Failed to load document</p>
              {!useDirectContent && (
                <Button size="sm" color="primary" variant="flat" onPress={() => void refetch()}>
                  Retry
                </Button>
              )}
            </div>
          )}

          {!isLoading && !error && !content?.content && (
            <div className="flex items-center justify-center py-16">
              <p className="text-muted">No content available</p>
            </div>
          )}

          {!isLoading && !error && content?.content && (
            <>
              {viewMode === 'raw' ? (
                <pre className="font-mono text-sm whitespace-pre-wrap break-words bg-surface-elevated rounded-lg p-4 text-foreground">
                  {content.content}
                </pre>
              ) : (
                <div className="prose dark:prose-invert max-w-none">
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      pre({ children }) {
                        return <>{children}</>;
                      },
                      code({ children, className, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const codeString = String(children).replace(/\n$/, '');

                        if (match?.[1] === 'mermaid') {
                          return <MermaidDiagram chart={codeString} />;
                        }

                        if (match) {
                          return <CodeBlock language={match[1]}>{codeString}</CodeBlock>;
                        }

                        // Fenced code block without language (ASCII wireframes, plain text)
                        const isBlock = codeString.includes('\n');
                        if (isBlock) {
                          return <CodeBlock>{codeString}</CodeBlock>;
                        }

                        return (
                          <code
                            className="px-1.5 py-0.5 rounded bg-surface-elevated text-sm font-mono"
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                      h1({ children }) {
                        const text = extractTextFromChildren(children);
                        return (
                          <h1
                            id={titleToId(text)}
                            className="text-2xl font-bold mt-8 mb-4 text-foreground"
                          >
                            {children}
                          </h1>
                        );
                      },
                      h2({ children }) {
                        const text = extractTextFromChildren(children);
                        return (
                          <h2
                            id={titleToId(text)}
                            className="text-xl font-semibold mt-6 mb-3 text-foreground border-b border-default pb-2"
                          >
                            {children}
                          </h2>
                        );
                      },
                      h3({ children }) {
                        const text = extractTextFromChildren(children);
                        return (
                          <h3
                            id={titleToId(text)}
                            className="text-lg font-semibold mt-5 mb-2 text-foreground"
                          >
                            {children}
                          </h3>
                        );
                      },
                      p({ children }) {
                        return <p className="mb-4 text-foreground leading-relaxed">{children}</p>;
                      },
                      ul({ children }) {
                        return (
                          <ul className="list-disc list-inside mb-4 space-y-1 text-foreground">
                            {children}
                          </ul>
                        );
                      },
                      ol({ children }) {
                        return (
                          <ol className="list-decimal list-inside mb-4 space-y-1 text-foreground">
                            {children}
                          </ol>
                        );
                      },
                      blockquote({ children }) {
                        return (
                          <blockquote className="border-l-4 border-primary pl-4 my-4 text-muted italic">
                            {children}
                          </blockquote>
                        );
                      },
                      table({ children }) {
                        return (
                          <div className="overflow-x-auto my-4">
                            <table className="min-w-full border-collapse border border-default">
                              {children}
                            </table>
                          </div>
                        );
                      },
                      thead({ children }) {
                        return <thead className="bg-surface-elevated">{children}</thead>;
                      },
                      th({ children }) {
                        return (
                          <th className="border border-default px-4 py-2 text-left text-sm font-semibold text-foreground">
                            {children}
                          </th>
                        );
                      },
                      td({ children }) {
                        return (
                          <td className="border border-default px-4 py-2 text-sm text-foreground">
                            {children}
                          </td>
                        );
                      },
                      a({ children, href }) {
                        if (!href) {
                          return <span className="text-primary">{children}</span>;
                        }
                        const isExternal = href.startsWith('http');
                        return (
                          <a
                            href={href}
                            className="text-primary hover:underline"
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {content.content}
                  </Markdown>
                </div>
              )}
            </>
          )}
        </div>

        {showToc && (
          <TocSidebar
            entries={tocEntries}
            isOpen={isTocOpen}
            onToggle={() => setIsTocOpen(prev => !prev)}
            onSelect={scrollToHeading}
          />
        )}
      </div>
    </Modal>
  );
}
